import {
  currencyRateConversion,
  formatAmount,
} from "../../../../../utils/hooks/dataHandler";
import {
  autoTextColor,
  beautyAutoLine,
  boldAutoColumn,
} from "../../../../../utils/pdf/page";
import {
  buildAutoTable,
  enhanceReferenceColumn,
  formatReferenceDetails,
} from "../../../../../utils/pdf/buildAutoTable";
import { createExchangeRateResolver } from "../../shared";
import { IRAS_MAIN_COMP_LINE_BY_ID, IRAS_MAIN_COMP_LINES } from "../lineConfig";
import {
  calculateIrasSgdSectionResults,
  getRenderableIrasMainCompSections,
} from "../sgdCalculation";
import {
  buildIrasSectionSpacer,
  getIrasTableCellPadding,
  getIrasTableStyles,
  normaliseIrasAutoTextColumnSpacing,
} from "../tableSpacing";
import { IRAS_TABLE_START_Y } from "../layoutConfig";
import {
  buildIrasCrossYAHeaders,
  getIrasCrossYAMainCompColumnStyles,
} from "./layoutConfig";

const YA_KEYS = ["preceding", "current"];

const selectYAAmounts = (rows, yaKey) =>
  (rows || []).map((row) => ({
    ...row,
    amount: row?.[`${yaKey}_amount`] ?? row?.amount,
  }));

const getSectionAmount = (section) =>
  section?.type === "result" ? section?.amount : section?.total;

const getRowKey = (row, occurrences) => {
  const baseKey = [row?.label, row?.schedule, row?.reference].join("|");
  const occurrence = occurrences.get(baseKey) || 0;
  occurrences.set(baseKey, occurrence + 1);
  return `${baseKey}|${occurrence}`;
};

const mergeRows = (precedingRows = [], currentRows = []) => {
  const mergedRows = [];
  const rowsByKey = new Map();

  YA_KEYS.forEach((yaKey) => {
    const occurrences = new Map();
    const rows = yaKey === "preceding" ? precedingRows : currentRows;

    rows.forEach((row) => {
      const key = getRowKey(row, occurrences);
      let mergedRow = rowsByKey.get(key);

      if (!mergedRow) {
        mergedRow = { key, preceding: null, current: null };
        rowsByKey.set(key, mergedRow);
        mergedRows.push(mergedRow);
      }

      mergedRow[yaKey] = row;
    });
  });

  return mergedRows;
};

const getSharedSectionLabel = (id, precedingSection, currentSection) => {
  const precedingLabel = precedingSection?.label;
  const currentLabel = currentSection?.label;

  if (!precedingLabel) return currentLabel || "";
  if (!currentLabel || precedingLabel === currentLabel) return precedingLabel;

  return IRAS_MAIN_COMP_LINE_BY_ID[id]?.label || precedingLabel;
};

const mergeSections = (precedingSections, currentSections) => {
  const precedingById = new Map(
    precedingSections.map((section) => [section.id, section]),
  );
  const currentById = new Map(
    currentSections.map((section) => [section.id, section]),
  );

  return IRAS_MAIN_COMP_LINES.map(({ id }) => {
    const preceding = precedingById.get(id);
    const current = currentById.get(id);
    if (!preceding && !current) return null;

    const availableSections = [preceding, current].filter(Boolean);
    const type = preceding?.type || current?.type;

    return {
      id,
      type,
      label: getSharedSectionLabel(id, preceding, current),
      preceding,
      current,
      rows: mergeRows(preceding?.rows, current?.rows),
      doubleLine: availableSections.some((section) => section.doubleLine),
      singleLine: availableSections.some((section) => section.singleLine),
      omitSubtotalRow: availableSections.every(
        (section) => section.omitSubtotalRow,
      ),
      suppressSubtotalLine: availableSections.every(
        (section) => section.suppressSubtotalLine,
      ),
      suppressTopLine: availableSections.every(
        (section) => section.suppressTopLine,
      ),
    };
  }).filter(Boolean);
};

const prepareYASections = ({
  currencyRate,
  hasSgdColumn,
  sections,
  unutilisedCapitalAllowances,
  unutilisedDonations,
  unutilisedLosses,
}) => {
  const renderedSections = getRenderableIrasMainCompSections(sections);
  let chargeableIncomeBeforeExemptAmount = null;
  let resultAmounts = new WeakMap();
  let sectionAmounts = new WeakMap();

  if (hasSgdColumn) {
    ({ chargeableIncomeBeforeExemptAmount, resultAmounts, sectionAmounts } =
      calculateIrasSgdSectionResults({
        currencyRate,
        sections: renderedSections,
        unutilisedCapitalAllowances,
        unutilisedDonations,
        unutilisedLosses,
      }));
  } else {
    const chargeableIncomeSection = renderedSections.find(
      (section) => section.id === "total_after_donations",
    );
    chargeableIncomeBeforeExemptAmount = Math.max(
      0,
      Math.round(parseFloat(chargeableIncomeSection?.amount) || 0),
    );
  }

  return {
    chargeableIncomeBeforeExemptAmount: chargeableIncomeBeforeExemptAmount ?? 0,
    renderedSections,
    resultAmounts,
    sectionAmounts,
  };
};

export const renderIrasCrossYAMainCompSections = ({
  company,
  currencyRate,
  currencyView,
  currentSections,
  forView,
  isCompact = false,
  pdf,
  precedingSections,
  reviewNote = "",
  schedule,
  tableSpacingOptions,
  unutilisedCapitalAllowances = [],
  unutilisedDonations = [],
  unutilisedLosses = [],
}) => {
  const body = [];
  const totalLines = [];
  const totalSubLines = [];
  const totalDoubleLines = [];
  const hasSgdColumn =
    String(currencyView ?? company?.currency ?? "SGD").toUpperCase() !== "SGD";
  const columnCount = 6 + (hasSgdColumn ? 2 : 0) + (forView ? 1 : 0);
  const spacingOptions = tableSpacingOptions ?? isCompact;
  const exchangeRate = currencyRate ?? company?.currencyRate ?? 1;
  const getExchangeRate = createExchangeRateResolver(exchangeRate);
  const convertToSgd = (amount, rowExchangeRate) =>
    formatAmount(
      Math.round(
        currencyRateConversion(
          parseFloat(amount) || 0,
          getExchangeRate(rowExchangeRate),
          "SGD",
        ),
      ),
    );
  const buildReferenceCell = (reference) =>
    autoTextColor(
      reference,
      [255, 0, 0],
      true,
      getIrasTableCellPadding(spacingOptions),
    );
  const prepared = {
    preceding: prepareYASections({
      currencyRate: exchangeRate,
      hasSgdColumn,
      sections: precedingSections,
      unutilisedCapitalAllowances: selectYAAmounts(
        unutilisedCapitalAllowances,
        "preceding",
      ),
      unutilisedDonations: selectYAAmounts(unutilisedDonations, "preceding"),
      unutilisedLosses: selectYAAmounts(unutilisedLosses, "preceding"),
    }),
    current: prepareYASections({
      currencyRate: exchangeRate,
      hasSgdColumn,
      sections: currentSections,
      unutilisedCapitalAllowances: selectYAAmounts(
        unutilisedCapitalAllowances,
        "current",
      ),
      unutilisedDonations: selectYAAmounts(unutilisedDonations, "current"),
      unutilisedLosses: selectYAAmounts(unutilisedLosses, "current"),
    }),
  };
  const renderedSections = mergeSections(
    prepared.preceding.renderedSections,
    prepared.current.renderedSections,
  );
  const lastResultSectionIndex = renderedSections.reduce(
    (lastIndex, section, index) =>
      section.type === "result" ? index : lastIndex,
    -1,
  );
  const addSgdCells = (row, precedingSgdAmount, currentSgdAmount) => {
    if (!hasSgdColumn) return row;

    row.splice(5, 0, currentSgdAmount);
    row.splice(3, 0, precedingSgdAmount);
    return row;
  };

  renderedSections.forEach((section, sectionIndex) => {
    if (
      sectionIndex > 0 &&
      sectionIndex !== lastResultSectionIndex &&
      section.id !== "net_profit_before_tax" &&
      section.type !== "result"
    ) {
      body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
    }

    if (section.type === "result") {
      if (!section.suppressTopLine) totalSubLines.push(body.length - 1);

      const precedingAmount = getSectionAmount(section.preceding);
      const currentAmount = getSectionAmount(section.current);
      const resultRow = [
        boldAutoColumn(section.label),
        "",
        formatAmount(precedingAmount || 0),
        "",
        formatAmount(currentAmount || 0),
        "",
      ];

      addSgdCells(
        resultRow,
        formatAmount(
          prepared.preceding.resultAmounts.get(section.preceding) || 0,
        ),
        formatAmount(prepared.current.resultAmounts.get(section.current) || 0),
      );
      body.push(resultRow);

      const previousSection = renderedSections[sectionIndex - 1];
      if (
        previousSection?.suppressSubtotalLine &&
        !previousSection?.omitSubtotalRow
      ) {
        const previousRowIndex = body.length - 2;
        if (
          previousRowIndex >= 0 &&
          !totalSubLines.includes(previousRowIndex)
        ) {
          totalSubLines.push(previousRowIndex);
        }
      }

      if (sectionIndex === lastResultSectionIndex || section.doubleLine) {
        totalSubLines.push(body.length - 1);
        totalDoubleLines.push(body.length - 1);
      } else if (section.singleLine) {
        totalSubLines.push(body.length - 1);
      }

      return;
    }

    const hasSingleRow = section.rows.length === 1;
    body.push([boldAutoColumn(section.label)]);

    section.rows.forEach((mergedRow) => {
      const precedingRow = mergedRow.preceding;
      const currentRow = mergedRow.current;
      const row = precedingRow || currentRow;
      const precedingAmount = precedingRow?.amount || 0;
      const currentAmount = currentRow?.amount || 0;
      const content = hasSingleRow
        ? [
            row?.label || "",
            "",
            formatAmount(precedingAmount),
            "",
            formatAmount(currentAmount),
            row?.schedule && `${row.schedule}`,
          ]
        : [
            row?.label || "",
            formatAmount(precedingAmount),
            "",
            formatAmount(currentAmount),
            "",
            row?.schedule && `${row.schedule}`,
          ];

      addSgdCells(
        content,
        convertToSgd(precedingAmount, precedingRow?.exchangeRate),
        convertToSgd(currentAmount, currentRow?.exchangeRate),
      );
      if (forView) {
        content.push(
          buildReferenceCell(precedingRow?.reference || currentRow?.reference),
        );
      }
      body.push(content);
    });

    if (hasSingleRow) return;
    if (section.rows.length > 0) totalLines.push(body.length - 1);

    const subtotalRow = [
      "",
      "",
      formatAmount(getSectionAmount(section.preceding) || 0),
      "",
      formatAmount(getSectionAmount(section.current) || 0),
      "",
    ];
    addSgdCells(
      subtotalRow,
      formatAmount(
        prepared.preceding.sectionAmounts.get(section.preceding) || 0,
      ),
      formatAmount(prepared.current.sectionAmounts.get(section.current) || 0),
    );

    if (!section.omitSubtotalRow) body.push(subtotalRow);
  });

  const referenceColumnIndex = hasSgdColumn ? 8 : 6;
  if (forView && body.length > 0) {
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      body,
      referenceColumnIndex,
      15,
    );
    body.length = 0;
    body.push(...enhancedBody);
    normaliseIrasAutoTextColumnSpacing(
      body,
      referenceColumnIndex,
      spacingOptions,
    );

    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNote !== "") reviewNote += "\n\n";
      reviewNote += "[Reference Details]\n" + refDetailsText;
    }
  }

  if (body.length > 0) {
    const detailLineColumns = hasSgdColumn ? [1, 4] : [1, 3];
    const totalLineColumns = hasSgdColumn ? [2, 5] : [2, 4];

    buildAutoTable({
      head: buildIrasCrossYAHeaders({
        company,
        currencyView,
        forView,
        hasSgdColumn,
      }),
      body,
      pdf,
      customStartY: IRAS_TABLE_START_Y,
      schedule,
      company,
      styles: getIrasTableStyles(pdf, spacingOptions),
      columnStyles: getIrasCrossYAMainCompColumnStyles(hasSgdColumn, forView),
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (totalLines.includes(data.row.index) &&
            detailLineColumns.includes(data.column.index)) ||
            (totalSubLines.includes(data.row.index) &&
              totalLineColumns.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            totalLineColumns.includes(data.column.index),
        );
      },
      lineSettings: {
        totalLines: totalSubLines,
        totalDoubleLines,
        subTotalLines: totalLines,
        totalRows: totalLineColumns,
        subTotalRows: detailLineColumns,
      },
    });
  }

  return {
    bodyRowCount: body.length,
    chargeableIncomeBeforeExemptAmount: {
      preceding: prepared.preceding.chargeableIncomeBeforeExemptAmount,
      current: prepared.current.chargeableIncomeBeforeExemptAmount,
    },
    reviewNote,
  };
};
